import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Music, ChevronRight } from 'lucide-react';

interface GenreCardProps {
  name: string;
  slug: string;
  count: number;
  holiday?: string; // Optional holiday filter to apply when clicking
  software?: string; // Optional software filter to apply when clicking
}

/**
 * Reusable genre card component for displaying genre information
 * with navigation to genre-filtered sequence pages.
 *
 * Can optionally include a holiday or software filter for filtered genre browsing.
 */
export const GenreCard: React.FC<GenreCardProps> = ({ name, slug, count, holiday, software }) => {
  // Build the href with optional holiday or software filter
  let href = `/sequences/genre/${slug}`;
  if (holiday) {
    href += `?holiday=${encodeURIComponent(holiday)}`;
  } else if (software) {
    href += `?software=${encodeURIComponent(software)}`;
  }

  return (
    <Link to={href}>
      <Card className="group hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer h-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                <Music className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold capitalize group-hover:text-primary transition-colors">{name}</h3>
                <p className="text-sm text-muted-foreground">{count} sequences</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
